// Copyright (c) Docugami, Inc. All rights reserved.

import { TokenRange } from "networking/api";
import { useCallback, useMemo } from "react";
import { Token, TokenMargins } from "utils/TokensTextParser";
import { useDocumentDgml } from "./documentDgml";

export interface ITokensText {
    getTextForTokenRange(range: TokenRange, compressSpaces: boolean): string[];
    getMarginsForTokenRange(range: TokenRange, leftMarginMinSize: number, rightMarginMinSize: number, breakOnNewLine: boolean, compressSpaces: boolean): TokenMargins;
    getNormalizedTextForDocument(): string;
    tokens: Token[];
    isInitialized: boolean;
}

/**
 * Hook for retrieving and interacting with tokens.txt
 * @param documentId The target document
 */
export const useTokensText = (): ITokensText => {
    const dgml = useDocumentDgml();

    const getTextForTokenRange = useCallback((range: TokenRange, compressSpaces: boolean): string[] => {
        if (!dgml.isInitialized()) {
            throw new Error("Attempted to getTextForTokenRange before dgml initialized.");
        }

        return dgml.getTokensTextParser().getTextForTokenRange(range, compressSpaces);
    }, [dgml]);

    const getMarginsForTokenRange = useCallback((range: TokenRange, leftMarginMinSize: number, rightMarginMinSize: number, breakOnNewLine: boolean, compressSpaces: boolean): TokenMargins => {
        if (!dgml.isInitialized()) {
            throw new Error("Attempted to getMarginsForTokenRange before dgml initialized.");
        }

        return dgml.getTokensTextParser().getMarginsForTokenRange(range, leftMarginMinSize, rightMarginMinSize, breakOnNewLine, compressSpaces);
    }, [dgml]);

    const getNormalizedTextForDocument = useCallback(() => {
        if (!dgml.isInitialized()) {
            return "";
        }

        return dgml.getTokensTextParser().getNormalizedText();
    }, [dgml]);

    const tokens = useMemo((): Token[] => {
        if (!dgml.isInitialized()) {
            return [];
        }

        return dgml.getTokensTextParser().tokens;
    }, [dgml]);

    return {
        getTextForTokenRange: getTextForTokenRange,
        getMarginsForTokenRange: getMarginsForTokenRange,
        getNormalizedTextForDocument: getNormalizedTextForDocument,
        tokens: tokens,
        isInitialized: dgml.isInitialized()
    }
}
